import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import { showToast } from './toast';

// Custom error class that carries response data for special business codes
export class ApiError extends Error {
  code: number;
  data?: unknown;
  constructor(msg: string, code: number | string, data?: unknown) {
    super(msg);
    const normalizedCode = Number(code);
    this.code = Number.isNaN(normalizedCode) ? -1 : normalizedCode;
    this.data = data;
  }
}

// Get browser timezone (e.g., "Asia/Tokyo", "America/New_York")
const getTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

// Generate X-Request-Id
// Format: um + 14-digit timestamp + 6-char random string
const generateRequestId = (): string => {
  const prefix = 'uw';
  const timestamp = new Date()
    .toISOString()
    .replace(/[-:T.Z]/g, '')
    .slice(0, 14);
  const randomStr = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${prefix}${timestamp}${randomStr}`;
};

// Create axios instance
const request = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
request.interceptors.request.use(
  config => {
    // Inject X-Request-Id
    config.headers['X-Request-Id'] = generateRequestId();

    // Add X-Time-Zone header for server-side time handling
    config.headers['X-Time-Zone'] = getTimeZone();

    // Get token from authStore and inject Authorization header
    const { token } = useAuthStore.getState();
    if (token) {
      config.headers['Authorization'] = token;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 防止重复触发 401 处理
let isHandling401 = false;

// 统一 401 处理：提示用户 → 清除登录态 → 跳转登录页
const handle401 = async (msg?: string) => {
  if (isHandling401) return;
  isHandling401 = true;

  // 显示会话过期提示
  showToast(msg || 'Session expired, please sign in again', 'error');

  const { logout } = useAuthStore.getState();
  await logout();

  // 延迟跳转，让用户看到提示
  setTimeout(() => {
    window.location.href = '/login';
  }, 1500);
};

// Response interceptor
request.interceptors.response.use(
  async response => {
    // For blob responses, check if it's actually an error (JSON response)
    if (response.config.responseType === 'blob') {
      const contentType = response.headers['content-type'];
      // If content-type is JSON, it's an error response
      if (typeof contentType === 'string' && contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        const responseCode = Number(errorData.code);
        if (responseCode !== 0) {
          return Promise.reject(
            new ApiError(errorData.msg || 'Request failed', responseCode, errorData.data)
          );
        }
        return errorData;
      }
      return response;
    }

    const { msg } = response.data;
    const code = Number(response.data.code);
    const config = response.config;

    // 业务层 401：token 失效，弹窗提示后跳转登录页
    // 排除登录请求本身，避免登录失败时误触发
    const isLoginEndpoint = config.url?.includes('/login');
    if (code === 401 && !isLoginEndpoint) {
      await handle401(msg);
      return Promise.reject(new ApiError(msg || 'Session expired, please sign in again', 401));
    }

    // Special business code 409: user exists but not in org — pass data through
    if (code === 409) {
      return Promise.reject(new ApiError(msg || 'Conflict', 409, response.data.data));
    }

    // TOS 确认业务码交给页面自行处理，避免重复 toast
    if (code === 46001) {
      return Promise.reject(
        new ApiError(msg || 'Terms confirmation required', 46001, response.data.data)
      );
    }

    // Business error handling (including login failures)
    if (code !== 0) {
      console.error('Request error:', msg);
      showToast(msg || 'Request failed', 'error');
      return Promise.reject(new ApiError(msg || 'Request failed', code, response.data.data));
    }

    return response.data;
  },
  async error => {
    // HTTP error handling
    if (error.response) {
      const { status, config, data } = error.response;

      // 401 Unauthorized — clear auth state and redirect to login
      // Exclude login and logout requests to avoid redirect loops
      if (status === 401 && !config.url?.includes('/login') && !config.url?.includes('/logout')) {
        await handle401();
      }

      // Extract error message from response data for other HTTP errors
      if (data && data.msg) {
        showToast(data.msg, 'error');
        return Promise.reject(new Error(data.msg));
      }

      console.error('HTTP error:', status, error.response.data);
    } else {
      console.error('Network error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default request;
